import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Avatar, AvatarImage, AvatarFallback } from '../components/ui/avatar';
import { Skeleton } from '../components/ui/skeleton';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Plane, Users, Crown, Edit, MapPin, Calendar, Zap, Check, Palette } from 'lucide-react';
import ImageUpload from '../components/ImageUpload';
import { motion } from 'framer-motion';
import SEOHead from '../components/SEOHead';
import { BadgeShowcase, BadgeRow, RankRoadmap, XPRing, getXpProgress } from '../components/GamificationUI';

import { API } from '../config';

// ─── Color Palette Config ─────────────────────────────────────────────────────
export const PALETTES = {
  orange:  { name: 'Sunset Orange', primary: '#FF4500', light: 'rgba(255,69,0,0.12)', border: 'rgba(255,69,0,0.3)', gradient: 'from-orange-500 to-red-500' },
  blue:    { name: 'Deep Blue',     primary: '#1E3A8A', light: 'rgba(30,58,138,0.12)', border: 'rgba(30,58,138,0.3)', gradient: 'from-blue-700 to-indigo-800' },
  green:   { name: 'Emerald',       primary: '#10B981', light: 'rgba(16,185,129,0.12)', border: 'rgba(16,185,129,0.3)', gradient: 'from-emerald-500 to-teal-600' },
  purple:  { name: 'Cosmic Purple', primary: '#7C3AED', light: 'rgba(124,58,237,0.12)', border: 'rgba(124,58,237,0.3)', gradient: 'from-violet-600 to-purple-700' },
  red:     { name: 'Crimson',       primary: '#DC2626', light: 'rgba(220,38,38,0.12)', border: 'rgba(220,38,38,0.3)', gradient: 'from-red-600 to-rose-600' },
  teal:    { name: 'Ocean Teal',    primary: '#0891B2', light: 'rgba(8,145,178,0.12)', border: 'rgba(8,145,178,0.3)', gradient: 'from-cyan-600 to-teal-700' },
  gold:    { name: 'Golden',        primary: '#D97706', light: 'rgba(217,119,6,0.12)', border: 'rgba(217,119,6,0.3)', gradient: 'from-amber-500 to-yellow-600' },
  rose:    { name: 'Rose',          primary: '#E11D48', light: 'rgba(225,29,72,0.12)', border: 'rgba(225,29,72,0.3)', gradient: 'from-rose-500 to-pink-600' },
};

export function getPalette(key) {
  return PALETTES[key] || PALETTES.orange;
}

const BADGE_ICONS = {
  'New Traveler': '🌱',
  'First Flight': '🛫',
  'Regular Flyer': '✈️',
  'Frequent Flyer': '🏆',
  'Jet Setter': '🌍',
  'Sky High': '🚀',
  'Community Star': '⭐',
  'Layover Legend': '⏳',
  'Gate Guru': '🚪',
  'Fly N Out Club Member': '👑',
  'Explorer': '🗺️',
  'Verified Traveler': '✅',
};

export default function Profile() {
  const { userId } = useParams();
  const { user: currentUser, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [flights, setFlights] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [editData, setEditData] = useState({ name: '', bio: '', avatar: '', color_palette: 'orange' });
  const [saving, setSaving] = useState(false);

  const isOwn = currentUser?.user_id === userId;

  useEffect(() => {
    if (!userId) return;
    setLoading(true);
    Promise.all([
      axios.get(`${API}/users/${userId}`),
      axios.get(`${API}/users/${userId}/flight-records`).catch(() => ({ data: [] })),
    ]).then(([profileRes, recordsRes]) => {
        const u = profileRes.data.user;
        setProfile(u);
        setFlights(recordsRes.data || []);
        setEditData({
          name: u.name || '',
          bio: u.bio || '',
          avatar: u.avatar || '',
          color_palette: u.color_palette || 'orange',
        });
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [userId]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/users/me`, editData, { withCredentials: true });
      await refreshUser();
      const r = await axios.get(`${API}/users/${userId}`);
      setProfile(r.data.user);
      setEditing(false);
      toast.success('Profile updated!');
    } catch {
      toast.error('Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  const handleFollow = async () => {
    if (!currentUser) { toast.error('Please log in'); return; }
    try {
      await axios.post(`${API}/users/${userId}/follow`, {}, { withCredentials: true });
      toast.success('Following!');
      const r = await axios.get(`${API}/users/${userId}`);
      setProfile(r.data.user);
    } catch { toast.error('Failed'); }
  };

  const isFollowing = currentUser && profile?.followers?.includes(currentUser.user_id);
  const palette = getPalette(profile?.color_palette);

  const formatDate = (iso) => {
    if (!iso) return '';
    return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
  };

  if (loading) {
    return (
      <div className="flex flex-col min-h-screen bg-background">
        <Navbar />
        <main className="flex-1">
          <div className="max-w-3xl mx-auto px-4 py-8 space-y-4">
            <Skeleton className="h-48 rounded-2xl" />
            <Skeleton className="h-32 rounded-2xl" />
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="flex flex-col min-h-screen bg-background">
        <Navbar />
        <main className="flex-1">
          <div className="max-w-3xl mx-auto px-4 py-16 text-center">
            <p className="text-muted-foreground">User not found</p>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="flex flex-col min-h-screen bg-background">
      <SEOHead
        title={`${profile.display_name || profile.name?.split(' ')[0]} — Fly N Out Profile`}
        description={`View ${profile.display_name}'s travel profile on Fly N Out. ${profile.flight_count || 0} flights logged, ${profile.xp || 0} XP earned. See badges, rank, and community contributions.`}
        url={`/profile/${userId}`}
        noIndex
      />
      <Navbar />
      <main className="flex-1">
        {/* Profile Hero Banner */}
        <div
          className={`h-28 sm:h-36 bg-gradient-to-r ${palette.gradient}`}
          style={{ opacity: 0.9 }}
        />

        <div className="max-w-3xl mx-auto px-4 sm:px-6 pb-10">
          {/* Profile Card - overlapping the banner */}
          <Card className="fly-card p-5 -mt-12 mb-5 relative">
            <div className="flex items-start gap-4 flex-wrap">
              {/* Avatar with palette border */}
              <div className="relative -mt-8 flex-shrink-0">
                <Avatar className="w-20 h-20 ring-4 ring-background">
                  <AvatarImage src={profile.avatar} />
                  <AvatarFallback
                    className="text-white text-2xl font-bold"
                    style={{ background: palette.primary }}
                  >
                    {(profile.display_name || profile.name)?.[0]}
                  </AvatarFallback>
                </Avatar>
                {profile.is_premium && (
                  <div className="absolute -bottom-1 -right-1 w-6 h-6 rounded-full bg-[#FF4500] flex items-center justify-center">
                    <Crown className="w-3.5 h-3.5 text-white" />
                  </div>
                )}
              </div>

              <div className="flex-1 min-w-0 pt-1">
                <div className="flex items-center gap-2 flex-wrap mb-0.5">
                  <h1 className="text-xl font-bold" style={{ fontFamily: 'Space Grotesk' }}>
                    {profile.display_name || profile.name?.split(' ')[0]}
                  </h1>
                  {/* Rank badge */}
                  {profile.rank_info?.name && (
                    <span
                      className="text-xs px-2 py-0.5 rounded-full border font-medium"
                      style={{ background: palette.light, borderColor: palette.border, color: palette.primary }}
                    >
                      {profile.rank_info.icon} {profile.rank_info.name}
                    </span>
                  )}
                </div>
                {profile.bio && <p className="text-sm text-muted-foreground mb-2 max-w-md">{profile.bio}</p>}
                <div className="flex gap-5 text-sm">
                  <div><span className="font-bold">{profile.flight_count || 0}</span> <span className="text-muted-foreground">flights</span></div>
                  <div><span className="font-bold">{profile.xp || 0}</span> <span className="text-muted-foreground">XP</span></div>
                  <div><span className="font-bold">{profile.followers?.length || 0}</span> <span className="text-muted-foreground">followers</span></div>
                  <div><span className="font-bold">{profile.following?.length || 0}</span> <span className="text-muted-foreground">following</span></div>
                </div>
              </div>

              {/* Action button */}
              <div className="flex-shrink-0">
                {isOwn ? (
                  <Dialog open={editing} onOpenChange={setEditing}>
                    <DialogTrigger asChild>
                      <Button size="sm" variant="outline" data-testid="edit-profile-btn">
                        <Edit className="w-3.5 h-3.5 mr-1" /> Edit
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="sm:max-w-md max-h-[90vh] overflow-y-auto">
                      <DialogHeader>
                        <DialogTitle style={{ fontFamily: 'Space Grotesk' }}>Edit Profile</DialogTitle>
                      </DialogHeader>
                      <div className="space-y-4">
                        <div>
                          <Label>Display Name</Label>
                          <Input
                            value={editData.name}
                            onChange={e => setEditData(p => ({ ...p, name: e.target.value }))}
                            placeholder="Your name"
                          />
                          <p className="text-xs text-muted-foreground mt-1">Shown as first name + 2-letter initial for privacy.</p>
                        </div>
                        <div>
                          <Label>Bio</Label>
                          <Textarea
                            rows={3}
                            value={editData.bio}
                            onChange={e => setEditData(p => ({ ...p, bio: e.target.value }))}
                            placeholder="Tell travelers about yourself"
                          />
                        </div>
                        <div>
                          <Label>Profile Photo</Label>
                          <div className="flex items-center gap-4 mt-2">
                            <ImageUpload
                              currentImage={editData.avatar}
                              onUpload={url => setEditData(p => ({ ...p, avatar: url || p.avatar }))}
                              shape="circle"
                              className="w-20 h-20 flex-shrink-0"
                              placeholder="Upload photo"
                            />
                            <p className="text-xs text-muted-foreground">Upload a photo (JPG, PNG, WebP — max 10MB)</p>
                          </div>
                        </div>

                        {/* Color Palette Selector */}
                        <div>
                          <Label className="flex items-center gap-1.5 mb-3">
                            <Palette className="w-4 h-4" /> Profile Color Palette
                          </Label>
                          <div className="grid grid-cols-4 gap-2.5">
                            {Object.entries(PALETTES).map(([key, pal]) => (
                              <button
                                key={key}
                                onClick={() => setEditData(p => ({ ...p, color_palette: key }))}
                                className={`group relative flex flex-col items-center gap-1.5 p-2.5 rounded-xl border-2 transition-all ${
                                  editData.color_palette === key
                                    ? 'border-foreground shadow-md scale-105'
                                    : 'border-border hover:border-muted-foreground'
                                }`}
                                title={pal.name}
                              >
                                {/* Color circle */}
                                <div
                                  className="w-8 h-8 rounded-full shadow-sm"
                                  style={{ background: `linear-gradient(135deg, ${pal.primary}, ${pal.primary}cc)` }}
                                />
                                <span className="text-[10px] text-center text-muted-foreground leading-tight">{pal.name}</span>
                                {editData.color_palette === key && (
                                  <div className="absolute top-1 right-1 w-4 h-4 rounded-full bg-foreground flex items-center justify-center">
                                    <Check className="w-2.5 h-2.5 text-background" />
                                  </div>
                                )}
                              </button>
                            ))}
                          </div>
                          {/* Preview */}
                          <div
                            className="mt-3 p-3 rounded-xl text-sm font-medium text-white text-center"
                            style={{ background: `linear-gradient(135deg, ${PALETTES[editData.color_palette]?.primary}, ${PALETTES[editData.color_palette]?.primary}bb)` }}
                          >
                            Preview: {PALETTES[editData.color_palette]?.name}
                          </div>
                        </div>

                        <Button className="fly-btn-primary w-full" onClick={handleSave} disabled={saving}>
                          {saving ? 'Saving...' : 'Save Changes'}
                        </Button>
                      </div>
                    </DialogContent>
                  </Dialog>
                ) : (
                  <Button
                    size="sm"
                    className={isFollowing ? '' : 'fly-btn-primary'}
                    variant={isFollowing ? 'outline' : 'default'}
                    onClick={handleFollow}
                    data-testid="follow-btn"
                  >
                    {isFollowing ? 'Following' : 'Follow'}
                  </Button>
                )}
              </div>
            </div>

            {/* Badges row — compact earned badges */}
            {profile.badges?.length > 0 && (
              <div className="mt-4 pt-4 border-t border-border">
                <p className="text-xs text-muted-foreground mb-2 font-medium uppercase tracking-wide">Travel Badges</p>
                <BadgeRow badges={profile.badges} max={5} />
              </div>
            )}
          </Card>

          {/* XP + Rank Progress */}
          {profile.rank_info && typeof profile.rank_info === 'object' && (
            <Card className="fly-card p-4 mb-5">
              <div className="flex items-center gap-4 mb-4">
                <XPRing xp={profile.xp || 0} size={80} strokeWidth={7} />
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-semibold text-sm" style={{ fontFamily: 'Space Grotesk' }}>
                      {profile.rank_info.icon} {profile.rank_info.name}
                    </span>
                    <span className="text-xs text-muted-foreground">{profile.xp || 0} XP total</span>
                  </div>
                  {profile.rank_info.next_rank ? (
                    <p className="text-xs text-muted-foreground mb-2">
                      {profile.rank_info.next_rank.xp_needed - (profile.xp || 0)} XP until{' '}
                      <span style={{ color: getXpProgress(profile.xp || 0).next?.color }}>
                        {profile.rank_info.next_rank.icon} {profile.rank_info.next_rank.name}
                      </span>
                    </p>
                  ) : (
                    <p className="text-xs font-medium text-[#FF4500] mb-2">Max Rank Achieved! 🌟</p>
                  )}
                  <RankRoadmap xp={profile.xp || 0} compact />
                </div>
              </div>
            </Card>
          )}

          {/* Premium promo */}
          {isOwn && !profile.is_premium && (
            <Card
              className="fly-card p-4 mb-5 border"
              style={{ borderColor: palette.border, background: palette.light }}
            >
              <div className="flex items-center justify-between gap-3">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <Crown className="w-4 h-4" style={{ color: palette.primary }} />
                    <span className="font-semibold text-sm" style={{ fontFamily: 'Space Grotesk' }}>Upgrade to Fly N Out Club</span>
                  </div>
                  <p className="text-xs text-muted-foreground">Unlock premium features, exclusive community perks, and more.</p>
                </div>
                <Button size="sm" className="fly-btn-primary flex-shrink-0" asChild>
                  <Link to="/premium">SAR 29/mo</Link>
                </Button>
              </div>
            </Card>
          )}

          {/* Tabs: Flight History + Achievements + Stats */}
          <Tabs defaultValue="flights">
            <TabsList className="mb-4">
              <TabsTrigger value="flights">
                <Plane className="w-3.5 h-3.5 mr-1.5" /> Flights ({flights.length})
              </TabsTrigger>
              <TabsTrigger value="achievements">
                <Zap className="w-3.5 h-3.5 mr-1.5" /> Achievements
              </TabsTrigger>
              <TabsTrigger value="stats">
                <Crown className="w-3.5 h-3.5 mr-1.5" /> Stats
              </TabsTrigger>
            </TabsList>

            {/* Flight History */}
            <TabsContent value="flights">
              {flights.length === 0 ? (
                <Card className="fly-card p-8 text-center">
                  <Plane className="w-8 h-8 text-muted-foreground mx-auto mb-2" />
                  <p className="text-muted-foreground">{isOwn ? 'Log your first flight!' : 'No flights logged yet'}</p>
                  {isOwn && (
                    <Button size="sm" className="fly-btn-primary mt-3" asChild>
                      <Link to="/dashboard">Log a Flight</Link>
                    </Button>
                  )}
                </Card>
              ) : (
                <div className="space-y-2">
                  {flights.map((f, i) => (
                    <motion.div
                      key={f.record_id || f.flight_id || i}
                      initial={{ opacity: 0, x: -8 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: i * 0.05 }}
                    >
                      <Link to={`/route/${f.origin_code}-${f.destination_code}`}>
                        <Card className="fly-card p-4 hover:shadow-md transition-shadow" data-testid="my-flights-flight-card">
                          <div className="flex items-center justify-between gap-3">
                            <div className="flex items-center gap-3">
                              <div
                                className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0"
                                style={{ background: palette.light }}
                              >
                                <Plane className="w-4 h-4" style={{ color: palette.primary }} />
                              </div>
                              <div>
                                <div className="font-bold text-sm" style={{ fontFamily: 'Space Grotesk' }}>
                                  {f.flight_number}
                                </div>
                                <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                                  <MapPin className="w-3 h-3" />
                                  {f.origin_code} <Plane className="w-2.5 h-2.5" /> {f.destination_code}
                                </div>
                              </div>
                            </div>
                            <div className="text-right">
                              {(f.flight_date || f.created_at) && (
                                <div className="text-xs text-muted-foreground flex items-center gap-1 justify-end">
                                  <Calendar className="w-3 h-3" />
                                  {formatDate(f.flight_date || f.created_at)}
                                </div>
                              )}
                              {f.seat && (
                                <div className="text-xs text-muted-foreground">Seat {f.seat}</div>
                              )}
                              <div className="text-xs mt-0.5" style={{ color: palette.primary }}>View route →</div>
                            </div>
                          </div>
                        </Card>
                      </Link>
                    </motion.div>
                  ))}
                </div>
              )}
            </TabsContent>

            {/* Achievements Tab */}
            <TabsContent value="achievements">
              <div className="space-y-6">
                {/* XP + Rank Roadmap */}
                <Card className="fly-card p-5">
                  <div className="flex items-center gap-2 mb-4">
                    <Zap className="w-4 h-4 text-[#FF4500]" />
                    <h3 className="font-semibold text-sm" style={{ fontFamily: 'Space Grotesk' }}>Rank Progression</h3>
                    <span className="ml-auto text-xs text-muted-foreground font-medium">{profile.xp || 0} XP total</span>
                  </div>
                  <RankRoadmap xp={profile.xp || 0} />
                </Card>

                {/* Badge Grid */}
                <Card className="fly-card p-5">
                  <div className="flex items-center gap-2 mb-4">
                    <Crown className="w-4 h-4 text-[#FF4500]" />
                    <h3 className="font-semibold text-sm" style={{ fontFamily: 'Space Grotesk' }}>All Badges</h3>
                    <span className="ml-auto text-xs text-muted-foreground">
                      {profile.badges?.length || 0} earned
                    </span>
                  </div>
                  <BadgeShowcase earnedBadges={profile.badges || []} showLocked={isOwn} />
                </Card>
              </div>
            </TabsContent>

            {/* Stats */}
            <TabsContent value="stats">
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {[
                  { label: 'Flights Logged', value: profile.flight_count || 0, icon: Plane },
                  { label: 'Total XP', value: profile.xp || 0, icon: Zap },
                  { label: 'Badges Earned', value: profile.badges?.length || 0, icon: Crown },
                  { label: 'Followers', value: profile.followers?.length || 0, icon: Users },
                  { label: 'Following', value: profile.following?.length || 0, icon: Users },
                  { label: 'Current Rank', value: profile.rank_info?.name || 'Boarding Pass', icon: Zap, isText: true },
                ].map(({ label, value, icon: Icon, isText }) => (
                  <Card key={label} className="fly-card p-4">
                    <Icon className="w-4 h-4 mb-1.5" style={{ color: palette.primary }} />
                    <div className={`font-bold ${isText ? 'text-sm' : 'text-2xl'}`} style={{ fontFamily: 'Space Grotesk', color: palette.primary }}>
                      {value}
                    </div>
                    <div className="text-xs text-muted-foreground mt-0.5">{label}</div>
                  </Card>
                ))}
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </main>
      <Footer />
    </div>
  );
}
